import { intersection, merge } from "lodash";
import { ref } from "vue";
import { array, BaseSchema, boolean, mixed, number, object, string } from "yup";

import { Validators } from "@/utilities/validations";
import { phoneNumber } from "@/utilities/validators";

import SpecialNeedsField from "./components/SpecialNeedsField.vue";
import { Application, FormOptions } from "./types";

export interface GeneratedField {
  comp: string;
  rules: Validators<unknown>;
  attrs: {
    [str: string]: unknown;
    name: keyof Application;
  };
}

const checkRequired = (schema: BaseSchema) => (required: boolean) => {
  if (!required) {
    return schema;
  }

  switch (schema.type) {
    case "array":
      //TODO: Make this check the schema type instead
      //@ts-ignore just ignore schema not supporting min
      return schema.min(1, "required");
    case "object":
      return schema.concat(object({ num: phoneNumber().required("required") }));
    default:
      return schema.required("required");
  }
};

const phone = object({
  num: phoneNumber(),
  type: mixed()
    .oneOf(["Work", "Home", "Other", "Cell"])
    .nullable()
    .when("num", {
      is: (val: string) => val.length,
      then: (schema) =>
        schema.required("You must specify what type of number this is"),
      otherwise: (schema) => schema,
    }),
});

export function useFieldGenerator(form: Application, formOptions: FormOptions) {
  const possibleFields = {
    schoolid: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    firstName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    middleName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    lastName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    gender: {
      comp: "BaseRadio",
      rules: checkRequired(mixed().oneOf(["M", "F"]).nullable()),
      attrs: {
        "option-id": "value",
        "option-label": "name",
        options: [
          { name: "Male", value: "M" },
          { name: "Female", value: "F" },
        ],
      },
    },
    dateOfBirth: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    highschool: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    classification: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    email: {
      comp: "BaseTextfield",
      rules: checkRequired(string().email().nullable()),
    },
    email2: {
      comp: "BaseTextfield",
      rules: checkRequired(string().email().nullable()),
    },
    homephone: {
      comp: "BaseTextfield",
      rules: checkRequired(phoneNumber().nullable()),
    },
    cellphone: {
      comp: "BaseTextfield",
      rules: checkRequired(phoneNumber().nullable()),
    },
    allowSms: {
      comp: "BaseRadio",
      rules: checkRequired(boolean().nullable()),
      attrs: {
        "option-id": "value",
        "option-label": "name",
        options: [
          { name: "Yes", value: true },
          { name: "No", value: false },
        ],
      },
    },
    isTransfer: {
      comp: "BaseDropdown",
      rules: checkRequired(boolean().nullable()),
      attrs: {
        "option-label": "name",
        "option-value": "value",
        options: [
          { name: "Yes", value: true },
          { name: "No", value: false },
        ],
      },
    },
    summerphone: {
      comp: "BaseTextfield",
      rules: checkRequired(phoneNumber().nullable()),
    },
    arrivalDt: {
      comp: "BaseDateTimePicker",
      rules: checkRequired(string().nullable()),
    },
    hasTransport: {
      comp: "BaseCheckBox",
      rules: checkRequired(boolean().nullable()),
      attrs: {
        single: true,
      },
    },
    ethnicId: {
      comp: "BaseDropdown",
      rules: checkRequired(number().nullable()),
      attrs: {
        options: formOptions.ethnicities,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    studentStatus: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.studentStatuses,
        "option-label": "label",
        "option-value": "value",
      },
    },
    // -----
    major: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    classLoad: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.classLoads,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    study: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.studyLevels,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    neatness: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.neatnessLevels,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    bedtime: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.bedtimes,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    waketime: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.waketimes,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    sleepLevel: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.sleepLevels,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    windows: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.windowsOptions,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    roomTemp: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.roomTemps,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    lifestyle: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.lifestyles,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    religious: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.religiousLevels,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    sabbathKeeping: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.sabbathKeepingOptions,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    hobbyIds: {
      comp: "BaseMultiSelect",
      rules: checkRequired(array().of(number()).nullable()),
      attrs: {
        options: formOptions.hobbies,
        "help-text": "choose all that apply",
        display: "chip",
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    // TODO: Clean this up, maybe make a better system for validations here
    likedMusicIds: {
      comp: "BaseMultiSelect",
      rules: (required: boolean) => [
        checkRequired(array().of(number()).nullable())(required),
        (v: number[]) =>
          v.length === 0 ||
          intersection(v, form.dislikedMusicIds).length === 0 ||
          "You cannot like and dislike a type of music",
      ],
      attrs: {
        options: formOptions.music,
        "help-text": "choose all that apply",
        display: "chip",
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    dislikedMusicIds: {
      comp: "BaseMultiSelect",
      rules: (required: boolean) => [
        checkRequired(array().of(number()).nullable())(required),
        (v: number[]) =>
          v.length === 0 ||
          intersection(v, form.likedMusicIds).length === 0 ||
          "You cannot like and dislike a type of music",
      ],
      attrs: {
        options: formOptions.music,
        "help-text": "choose all that apply",
        display: "chip",
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    musicTime: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.musicTimes,
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    possessedQualityIds: {
      comp: "BaseMultiSelect",
      rules: checkRequired(array().of(number()).nullable()),
      attrs: {
        options: formOptions.qualities,
        "help-text": "choose all that apply",
        display: "chip",
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    desiredQualityIds: {
      comp: "BaseMultiSelect",
      rules: checkRequired(array().of(number()).nullable()),
      attrs: {
        options: formOptions.qualities,
        "help-text": "choose all that apply",
        display: "chip",
        "option-label": "label",
        "option-value": "value",
        "show-clear": true,
      },
    },
    // ----
    guardian1Address1: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1Address2: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1CellPhone: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1City: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1Country: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1Email: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1FirstName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1HomePhone: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1LastName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1OtherPhone: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1WorkPhone: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1Postal: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1Relation: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardian1State: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    guardianPhone1: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    guardianPhone2: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    guardianPhone3: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    guardianPhone4: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    // ----

    emergencyContact1FirstName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    emergencyContact1LastName: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    emergencyContact1Relation: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    emergencyContact1Address1: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    emergencyContact1Address2: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    emergencyContact1City: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "col-md-6",
      },
    },
    emergencyContact1State: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "col-md-3",
      },
    },
    emergencyContact1Postal: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "col-md-3",
      },
    },
    emergencyContact1Country: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    emergencyContact1Phone1: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    emergencyContact1Phone2: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    emergencyContact1Phone3: {
      comp: "BasePhoneField",
      rules: checkRequired(phone),
    },
    // ----
    homeAddress1: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "mb-3",
      },
    },
    homeAddress2: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "mb-3",
      },
    },
    homeCity: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "mb-3 col-md-6",
      },
    },
    homeState: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.states,
        "option-label": "label",
        "option-value": "value",
        "div-class": "mb-3 col-md-3",
      },
    },
    homePostal: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "mb-3 col-md-3",
      },
    },
    homeCountry: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
      attrs: {
        "div-class": "mb-3",
      },
    },
    // ---
    otherPrefs: {
      comp: "BaseTextArea",
      rules: checkRequired(string().nullable()),
    },
    comments: {
      comp: "BaseTextfield",
      rules: checkRequired(string().nullable()),
    },
    specialNeeds: {
      comp: SpecialNeedsField,
      rules: () => string().nullable().required("required"),
    },
    buildingPref: {
      comp: "BaseDropdown",
      rules: checkRequired(string().nullable()),
      attrs: {
        options: formOptions.buildings,
        "option-label": "label",
        "option-value": "value",
      },
    },
  };

  const formFields = (fieldNames: string[]) =>
    fieldNames
      .map((fieldName) => ({
        name: fieldName,
        spec: formOptions.fieldSpecs[fieldName],
      }))
      .filter(({ spec }) => spec)
      .map(({ name, spec }) => ({
        name,
        spec,
        field: possibleFields[name as keyof typeof possibleFields],
      }))
      .map(
        ({ name, spec, field }) =>
          merge(field, {
            rules: field.rules(spec.formState === "Required"),
            attrs: {
              name,
              disabled: spec.formState === "Readonly",
              optional: spec.formState === "Optional",
              label: spec.label,
            },
          }) as GeneratedField,
      );

  const studentInfoFields = formFields([
    "schoolid",
    "firstName",
    "middleName",
    "lastName",
    "gender",
    "dateOfBirth",
    "highschool",
    "classification",
    "email",
    "email2",
    "homephone",
    "cellphone",
    "allowSms",
    "summerphone",
    "ethnicId",
    "studentStatus",
    "isTransfer",
    "arrivalDt",
    "hasTransport",
  ]);
  const addressFields = formFields([
    "homeAddress1",
    "homeAddress2",
    "homeCity",
    "homeState",
    "homePostal",
    "homeCountry",
  ]);
  const aboutMeFields = formFields([
    "major",
    "classLoad",
    "study",
    "neatness",
    "bedtime",
    "waketime",
    "sleepLevel",
    "windows",
    "roomTemp",
    "lifestyle",
    "religious",
    "sabbathKeeping",
    "hobbyIds",
    "musicTime",
    "likedMusicIds",
    "dislikedMusicIds",
    "possessedQualityIds",
    "desiredQualityIds",
  ]);
  const guardianFields = formFields([
    "guardian1FirstName",
    "guardian1LastName",
    "guardian1Relation",
    "guardian1Email",
    "guardian1HomePhone",
    "guardian1CellPhone",
    "guardian1WorkPhone",
    "guardian1OtherPhone",
    "guardianPhone1",
    "guardianPhone2",
    "guardianPhone3",
    "guardianPhone4",
    "guardian1Address1",
    "guardian1Address2",
    "guardian1City",
    "guardian1State",
    "guardian1Postal",
    "guardian1Country",
  ]);
  const roomPreferenceFields = formFields([
    "buildingPref",
    "comments",
    "otherPrefs",
    "specialNeeds",
  ]);

  const emergencyContactFields = formFields([
    "emergencyContact1FirstName",
    "emergencyContact1LastName",
    "emergencyContact1Relation",
    "emergencyContact1Address1",
    "emergencyContact1Address2",
    "emergencyContact1City",
    "emergencyContact1State",
    "emergencyContact1Postal",
    "emergencyContact1Country",
    "emergencyContact1Phone1",
    "emergencyContact1Phone2",
    "emergencyContact1Phone3",
  ]);

  return {
    studentInfoFields,
    addressFields,
    aboutMeFields,
    roomPreferenceFields,
    emergencyContactFields,
    guardianFields,
  };
}
